import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { RAW_ANNOTATION_TAG_NAMES } from "@perceptron/core/types";

/**
 * How much text the reveal keeps in hand, as time at the rate text is arriving. Text comes in network
 * chunks with gaps between them; a reveal that empties its buffer stops at every gap and then rushes
 * the next chunk, which reads as stop-and-go. Holding this much back rides over the gaps.
 */
export const PACED_BUFFER_MS = 300;
/** The window the arrival rate is measured over. */
export const PACED_RATE_WINDOW_MS = 1000;
/**
 * The fastest the reveal goes while the model streams, about 150 words a second. The model writes faster
 * than that; uncapped, several words land every frame and fade in as a clump. Capped, the reveal falls a
 * little behind on long answers and catches up once the run ends.
 */
export const PACED_MAX_CHARS_PER_SECOND = 900;
/** Once the run has ended, the rest is revealed at this multiple of the cap: quicker, but still word by word. */
export const PACED_FINISH_SPEEDUP = 2;
/** The slowest the reveal goes, so it never crawls while the model pauses (a tool call, say). */
export const PACED_MIN_CHARS_PER_SECOND = 60;
/** How long a word's entrance lasts; the words keep their animation this long after the reveal catches up. */
export const PACED_SETTLE_MS = 450;

/** Tags that arrive whole: they have their own entrances, and pacing their coordinates only delays them. */
const WHOLE_TAGS = RAW_ANNOTATION_TAG_NAMES;
const OPEN_TAG = new RegExp(`^<(${WHOLE_TAGS.join("|")})(?=[\\s/>])`);

/**
 * Where the reveal may stop once the pace has reached `target`: past the whole of any annotation tag
 * it reaches (to the end of the text while that tag is still open, since it shows only once closed),
 * and at the end of a word rather than inside one.
 */
export function revealEnd(text: string, from: number, target: number): number {
  let end = from;
  const limit = Math.min(Math.floor(target), text.length);
  // Not advancing (held, or less than a character to go) reveals nothing; snapping to the end of the word
  // from here would show a word the pace has not reached.
  if (limit <= from) return from;
  while (end < limit) {
    const tag = text[end] === "<" ? OPEN_TAG.exec(text.slice(end, end + 16)) : null;
    if (tag) {
      const close = text.indexOf(`</${tag[1]}>`, end);
      end = close === -1 ? text.length : close + tag[1]!.length + 3;
    } else {
      end += 1;
    }
  }
  while (end < text.length && !/\s/.test(text[end]!) && text[end] !== "<") end += 1;
  return end;
}

/**
 * How fast to reveal, in characters per millisecond: the rate text is arriving, nudged up while more
 * than the buffer is waiting and down while less is, so the buffer holds steady and the reveal never
 * stalls between chunks, and never faster than the cap. After the run it finishes what is left at
 * `PACED_FINISH_SPEEDUP` times the cap.
 */
export function pacedSpeed(backlog: number, arrivalRate: number, isStreaming: boolean): number {
  const floor = PACED_MIN_CHARS_PER_SECOND / 1000;
  const cap = PACED_MAX_CHARS_PER_SECOND / 1000;
  if (!isStreaming) return cap * PACED_FINISH_SPEEDUP;
  const buffer = arrivalRate * PACED_BUFFER_MS;
  const pressure = buffer > 0 ? Math.min(Math.max(backlog / buffer, 0.5), 3) : 1;
  return Math.min(cap, Math.max(floor, arrivalRate * pressure));
}

/**
 * Streamed text revealed at an even pace rather than in the bursts it arrives in. A fast model hands
 * over a paragraph a frame, and anything that animates the new words then animates it as one block;
 * paced, the words come in one after another at the speed the text is arriving, a moment behind it.
 *
 * Only text that streams in while the view is mounted is paced: text already there when it mounts, or
 * that arrives while nothing streams, shows at once. When the run ends the reveal finishes what it has instead of
 * jumping. `isRevealing` stays true until the last words have finished their entrance.
 */
export function usePacedText(
  text: string,
  isStreaming: boolean,
  /** Hold the reveal where it is, e.g. until text that comes before this has been shown. */
  isHeld = false,
): { text: string; isRevealing: boolean } {
  const [revealed, setRevealed] = useState(text.length);
  const [isRevealing, setIsRevealing] = useState(isStreaming);
  const textRef = useRef(text);
  const streamingRef = useRef(isStreaming);
  const revealedRef = useRef(revealed);
  const cursorRef = useRef(revealed);
  const pacingRef = useRef(isStreaming);
  const frameRef = useRef<number | null>(null);
  // When each piece of text arrived and how long it was, over the last rate window.
  const arrivalsRef = useRef<{ at: number; chars: number }[]>([]);
  const arrivedRef = useRef(text.length);
  const heldRef = useRef(isHeld);
  textRef.current = text;
  streamingRef.current = isStreaming;
  heldRef.current = isHeld;

  const reveal = (end: number) => {
    revealedRef.current = end;
    setRevealed(end);
  };

  useLayoutEffect(() => {
    // A new run starts from nothing.
    if (text.length < revealedRef.current) {
      cursorRef.current = text.length;
      reveal(text.length);
    }
    if (text.length < arrivedRef.current) arrivalsRef.current = [];
    if (text.length > arrivedRef.current) {
      arrivalsRef.current.push({ at: performance.now(), chars: text.length - arrivedRef.current });
    }
    arrivedRef.current = text.length;
    if (isStreaming) {
      pacingRef.current = true;
      setIsRevealing(true);
    } else if (!pacingRef.current && revealedRef.current !== text.length) {
      cursorRef.current = text.length;
      reveal(text.length);
    } else if (revealedRef.current >= text.length) {
      // The run ended with the reveal already caught up.
      pacingRef.current = false;
    }
    if (!pacingRef.current || frameRef.current !== null || revealedRef.current >= text.length) return;

    // Characters per millisecond over the rate window, measured from the first arrival in it.
    const arrivalRate = (now: number) => {
      const arrivals = arrivalsRef.current;
      while (arrivals.length > 0 && now - arrivals[0]!.at > PACED_RATE_WINDOW_MS) arrivals.shift();
      if (arrivals.length === 0) return 0;
      const chars = arrivals.reduce((sum, arrival) => sum + arrival.chars, 0);
      return chars / Math.max(now - arrivals[0]!.at, PACED_BUFFER_MS);
    };

    let last = performance.now();
    const step = (now: number) => {
      const current = textRef.current;
      const elapsed = Math.min(now - last, 100);
      last = now;
      const advance = heldRef.current
        ? 0
        : pacedSpeed(current.length - cursorRef.current, arrivalRate(now), streamingRef.current) * elapsed;
      const end = revealEnd(current, revealedRef.current, cursorRef.current + advance);
      cursorRef.current = Math.max(cursorRef.current + advance, end);
      if (end !== revealedRef.current) reveal(end);

      if (end < current.length) {
        frameRef.current = requestAnimationFrame(step);
        return;
      }
      frameRef.current = null;
      if (!streamingRef.current) pacingRef.current = false;
    };
    frameRef.current = requestAnimationFrame(step);
  }, [text, isStreaming]);

  // Let the last words finish their entrance before the text is plain again.
  useEffect(() => {
    if (isStreaming || revealed < text.length) return;
    const timer = setTimeout(() => setIsRevealing(false), PACED_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [isStreaming, revealed, text.length]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  return { text: text.slice(0, Math.min(revealed, text.length)), isRevealing };
}
